import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  inject,
  Injectable,
  makeStateKey,
  PLATFORM_ID,
  signal,
  TransferState,
} from '@angular/core';
import { catchError, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { CacheService } from '../../ssr-services/cache/cache.service';
import { REDIS_CLIENT_TOKEN } from '../../tokens/server-tokens';

export const CURRENCY_SYMBOLS = makeStateKey<Record<string, string>>('CURRENCY_SYMBOLS');

@Injectable({
  providedIn: 'root',
})
export class CurrencySymbolsService {
  public currencySymbols = signal<Record<string, string>>({});

  protected cacheService = inject(CacheService);

  protected http = inject(HttpClient);

  protected platform = inject(PLATFORM_ID);

  protected transferState = inject(TransferState);

  protected redisClient = inject(REDIS_CLIENT_TOKEN, { optional: true });

  constructor() {
    if (isPlatformBrowser(this.platform)) {
      this.currencySymbols.set(this.transferState.get(CURRENCY_SYMBOLS, {}));
    }
  }

  public fetchCurrencySymbols(): Observable<Record<string, string>> {
    const cacheContent = this.cacheService.cachedContent();

    if (!cacheContent) {
      throw new Error('Cache content not set');
    }

    const redisClient = this.redisClient;

    if (!redisClient) {
      throw new Error('Redis client not available');
    }

    const host = this.cacheService.getHost();
    let saveCache = true;

    return from(redisClient.getKey<Record<string, string>>(`${host}.currencies`))
      .pipe(
        switchMap((result) => {
          return result
            ? of(result)
            : this.http
                .get<
                  Record<string, string>
                >(`${cacheContent.cache.config.webAppConfig.s3BucketAssets}/${cacheContent.cache.customerId}/lang_common/currencies.json`)
                .pipe(
                  tap({
                    next: (getResult) => {
                      if (saveCache) {
                        redisClient
                          .setKey(`${host}.currencies`, getResult)
                          .then(() =>
                            console.log(`CurrencySymbols: currencies cached for host ${host}`)
                          );
                      }
                    },
                  }),
                  catchError((err) => {
                    console.error(err);
                    saveCache = false;
                    return this.http
                      .get<
                        Record<string, string>
                      >(`${cacheContent.cache.config.webAppConfig.s3BucketAssets}/global/lang_common/currencies.json`)
                      .pipe(
                        catchError((fallbackErr) => {
                          console.debug(fallbackErr);
                          console.warn(
                            `CurrencySymbols: unable to fetch currency symbols for host ${host} with customer id ${cacheContent.cache.customerId}`
                          );
                          console.error(
                            new Error(
                              `Status: ${fallbackErr.response?.status}; message: ${JSON.stringify(fallbackErr.response?.data)}`
                            )
                          );
                          return of({});
                        })
                      );
                  })
                );
        })
      )
      .pipe(
        map((result) => {
          this.transferState.set(CURRENCY_SYMBOLS, result);
          this.currencySymbols.set(result);

          return result;
        })
      );
  }
}
